import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Input, Space, Spin, message } from 'antd';
import { BulbOutlined, ArrowLeftOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AdvicePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [advice, setAdvice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 检查用户是否已登录
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
  }, [authState.isLoading, authState.isAuthenticated, navigate]);

  // 返回主页
  const handleBack = () => {
    navigate('/');
  };

  // 提交建议
  const handleSubmit = async () => {
    if (!advice.trim()) {
      message.warning('请输入您的建议');
      return;
    }

    if (!authState.token) {
      message.error('请先登录');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.addAdvice(
        { advice: advice.trim() },
        authState.token
      );

      if (response.status === 200) {
        message.success('建议提交成功！感谢您的宝贵意见');
        alert('✅ 提交成功！\n\n您的建议已成功提交，感谢您的宝贵意见！\n如果您的建议被采纳，将获得一张咖啡优惠券。');
        setAdvice(''); // 清空输入框
      } else {
        message.error(response.msg || '提交建议失败');
      }
    } catch (error) {
      console.error('提交建议失败:', error);
      message.error('提交建议失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 如果正在加载
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录
  if (!authState.isAuthenticated) {
    return null;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '800px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '40px'
      }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          size="large"
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          返回主页
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <BulbOutlined style={{ fontSize: '32px', color: 'white' }} />
          <Title level={2} style={{ color: 'white', margin: 0 }}>
            提交建议
          </Title>
        </div>
        <div style={{ width: '100px' }} /> {/* 占位符保持居中 */}
      </div>

      {/* 主卡片 */}
      <Card style={{
        width: '100%',
        maxWidth: '800px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 标题和说明 */}
          <div>
            <Title level={3} style={{ marginBottom: '12px' }}>
              您的意见对我们很重要
            </Title>
            <Text style={{ color: '#666', fontSize: '15px', display: 'block', lineHeight: '1.6' }}>
              我们致力于为您提供更好的服务体验。如果您在使用过程中有任何建议或想法，
              请在下方留言告诉我们。您的每一条建议都会被认真对待！
            </Text>
          </div>

          {/* 输入框 */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '15px' }}>
              请输入您的建议：
            </Text>
            <TextArea
              value={advice}
              onChange={(e) => setAdvice(e.target.value)}
              placeholder="在这里输入您的建议或意见...&#10;例如：界面设计、功能优化、新功能需求等"
              rows={8}
              maxLength={1000}
              showCount
              style={{
                fontSize: '15px',
                borderRadius: '8px'
              }}
            />
          </div>

          {/* 提交按钮 */}
          <Button
            type="primary"
            size="large"
            icon={<SendOutlined />}
            loading={submitting}
            onClick={handleSubmit}
            block
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
              background: '#1890ff',
              borderColor: '#1890ff'
            }}
          >
            提交建议
          </Button>
        </Space>
      </Card>

      {/* 温馨提示 */}
      <div style={{ 
        marginTop: '40px', 
        maxWidth: '800px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <Title level={5} style={{ color: '#1890ff', marginBottom: '12px' }}>
          💡 温馨提示
        </Title>
        <div style={{ textAlign: 'left', lineHeight: '1.8' }}>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            • 您的建议将被医生团队审核，被采纳的建议可获得一张咖啡优惠券作为奖励
          </Text>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
            • 请尽量详细描述您的建议，这将帮助我们更好地理解和改进
          </Text>
          <Text style={{ color: '#595959', fontSize: '14px', display: 'block' }}>
            • 每条建议我们都会认真对待，感谢您的支持与关注！
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AdvicePage;

